import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { verificationService } from '../../services/verificationService';
import { twoFactorService } from '../../services/twoFactorService';
import { notificationService } from '../../services/notificationService';
import { Mail, Bell, Lock, Database, User, Save, AlertCircle, CheckCircle, Shield, Key, Check, Smartphone, Edit2, X, RefreshCw, MessageSquareOff } from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Header from '../../components/ui/Header';

const UserSettingsPreferences = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('profile');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Profile state
  const [profile, setProfile] = useState({
    full_name: '',
    email: '',
    phone: '',
    farm_name: '',
    farm_size: '',
    operation_type: '',
    role: ''
  });

  // Notification preferences state
  const [notifications, setNotifications] = useState({
    email_enabled: true,
    sms_enabled: false,
    in_app_enabled: true,
    frequency: 'real-time',
    alerts_enabled: true,
    reports_enabled: true,
    updates_enabled: true
  });

  // Security state
  const [security, setSecurity] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    twoFactorEnabled: false,
    twoFactorMethod: 'sms'
  });

  // Add 2FA specific state after security state
  const [twoFactorState, setTwoFactorState] = useState({
    setupModal: false,
    backupCodesModal: false,
    backupCodes: [],
    copiedCodes: false,
    loading: false
  });

  // Data management state
  const [dataManagement, setDataManagement] = useState({
    retentionPeriod: '1-year',
    dataSharing: false,
    analyticsEnabled: true
  });

  // Add verification state
  const [verificationStatus, setVerificationStatus] = useState({
    emailVerified: false,
    phoneVerified: false
  });
  const [verificationModal, setVerificationModal] = useState({
    isOpen: false,
    type: null, // 'email' or 'sms'
    verificationId: null,
    code: ''
  });
  const [verificationLoading, setVerificationLoading] = useState(false);

  const [notificationPrefs, setNotificationPrefs] = useState({
    enable_phone_notifications: true,
    enable_email_notifications: true,
    enable_email_summaries: true,
    email_summary_frequency: 'daily',
    enable_audit_alerts: true,
    enable_compliance_alerts: true,
    urgent_alert_email: true,
    urgent_alert_sms: true
  });
  const [notificationPrefsLoading, setNotificationPrefsLoading] = useState(false);
  const [notificationPrefsError, setNotificationPrefsError] = useState('');
  const [notificationPrefsSuccess, setNotificationPrefsSuccess] = useState('');

  // SMS management state
  const [smsSettings, setSmsSettings] = useState({
    smsConsent: false,
    smsConsentUpdatedAt: null,
    phoneVerified: false
  });
  const [smsPhoneEdit, setSmsPhoneEdit] = useState({
    isEditing: false,
    newPhone: '',
    saving: false
  });
  const [smsOptOutModal, setSmsOptOutModal] = useState(false);
  const [smsLoading, setSmsLoading] = useState(false);
  const [smsMessage, setSmsMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchUserSettings();
  }, [user]);

  useEffect(() => {
    fetchVerificationStatus();
  }, [user]);

  useEffect(() => {
    if (user?.id) {
      loadNotificationPreferences();
      fetchSmsSettings();
    }
  }, [user]);

  const fetchUserSettings = async () => {
    try {
      setLoading(true);
      
      // Fetch user profile
      const { data: profileData, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.select('*')
        ?.eq('id', user?.id)
        ?.single();

      if (profileError) throw profileError;

      if (profileData) {
        setProfile({
          full_name: profileData?.full_name || '',
          email: user?.email || '',
          phone: profileData?.phone || '',
          farm_name: profileData?.farm_name || '',
          farm_size: profileData?.farm_size || '',
          operation_type: profileData?.operation_type || '',
          role: profileData?.role || 'user'
        });

        // Set security state with 2FA settings
        setSecurity(prev => ({
          ...prev,
          twoFactorEnabled: profileData?.two_factor_enabled || false,
          twoFactorMethod: profileData?.two_factor_method || 'sms'
        }));

        // Set notification preferences from metadata
        if (profileData?.notification_preferences) {
          setNotifications(profileData?.notification_preferences);
        }
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      setMessage({ type: 'error', text: error?.message || 'Failed to load settings' });
    } finally {
      setLoading(false);
    }
  };

  const fetchVerificationStatus = async () => {
    try {
      const status = await verificationService?.getVerificationStatus(user?.id);
      setVerificationStatus(status);
    } catch (error) {
      console.error('Error fetching verification status:', error);
    }
  };

  const loadNotificationPreferences = async () => {
    try {
      setNotificationPrefsLoading(true);
      const prefs = await notificationService?.getNotificationPreferences(user?.id);
      setNotificationPrefs(prefs);
    } catch (err) {
      console.error('Error loading notification preferences:', err);
      setNotificationPrefsError('Failed to load notification preferences');
    } finally {
      setNotificationPrefsLoading(false);
    }
  };

  const handleNotificationPrefsChange = (field, value) => {
    setNotificationPrefs(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveNotificationPrefs = async () => {
    try {
      setNotificationPrefsLoading(true);
      setNotificationPrefsError('');
      setNotificationPrefsSuccess('');

      await notificationService?.updateNotificationPreferences(user?.id, notificationPrefs);
      
      setNotificationPrefsSuccess('Notification preferences saved successfully');
      setTimeout(() => setNotificationPrefsSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving notification preferences:', err);
      setNotificationPrefsError(err?.message || 'Failed to save notification preferences');
    } finally {
      setNotificationPrefsLoading(false);
    }
  };

  const handleSendVerification = async (type) => {
    try {
      setVerificationLoading(true);
      setMessage({ type: '', text: '' });

      let result;
      if (type === 'email') {
        result = await verificationService?.sendEmailVerification(profile?.email);
        setMessage({ type: 'success', text: 'Verification code sent to your email' });
      } else {
        if (!profile?.phone) {
          setMessage({ type: 'error', text: 'Please add a phone number first' });
          return;
        }
        result = await verificationService?.sendSmsVerification(profile?.phone);
        setMessage({ type: 'success', text: 'Verification code sent via SMS' });
      }

      setVerificationModal({
        isOpen: true,
        type: type,
        verificationId: result?.verificationId,
        code: ''
      });
    } catch (error) {
      setMessage({ type: 'error', text: error?.message || 'Failed to send verification code' });
    } finally {
      setVerificationLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    try {
      setVerificationLoading(true);
      setMessage({ type: '', text: '' });

      await verificationService?.verifyCode(
        verificationModal?.verificationId,
        verificationModal?.code
      );

      setMessage({ 
        type: 'success', 
        text: `${verificationModal?.type === 'email' ? 'Email' : 'Phone'} verified successfully!` 
      });

      setVerificationModal({ isOpen: false, type: null, verificationId: null, code: '' });
      fetchVerificationStatus();
    } catch (error) {
      setMessage({ type: 'error', text: error?.message || 'Invalid verification code' });
    } finally {
      setVerificationLoading(false);
    }
  };

  const handleProfileChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
  };

  const handleNotificationChange = (field, value) => {
    setNotifications(prev => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
  };

  const handleSecurityChange = (field, value) => {
    setSecurity(prev => ({ ...prev, [field]: value }));
  };

  const handleDataManagementChange = (field, value) => {
    setDataManagement(prev => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
  };

  const saveProfileSettings = async () => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      const { error } = await supabase?.from('user_profiles')?.update({
          full_name: profile?.full_name,
          phone: profile?.phone,
          farm_name: profile?.farm_name,
          farm_size: profile?.farm_size,
          operation_type: profile?.operation_type,
          role: profile?.role,
          updated_at: new Date()?.toISOString()
        })?.eq('id', user?.id);

      if (error) throw error;

      setMessage({ type: 'success', text: 'Profile updated successfully!' });
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error saving profile:', error);
      setMessage({ type: 'error', text: error?.message || 'Failed to save profile' });
    } finally {
      setSaving(false);
    }
  };

  const saveNotificationSettings = async () => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      const { error } = await supabase?.from('user_profiles')?.update({
          notification_preferences: notifications,
          updated_at: new Date()?.toISOString()
        })?.eq('id', user?.id);

      if (error) throw error;

      setMessage({ type: 'success', text: 'Notification preferences saved successfully!' });
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error saving notifications:', error);
      setMessage({ type: 'error', text: error?.message || 'Failed to save notification preferences' });
    } finally {
      setSaving(false);
    }
  };

  const updatePassword = async () => {
    try {
      if (security?.newPassword !== security?.confirmPassword) {
        setMessage({ type: 'error', text: 'Passwords do not match' });
        return;
      }

      if (security?.newPassword?.length < 6) {
        setMessage({ type: 'error', text: 'Password must be at least 6 characters' });
        return;
      }

      setSaving(true);
      setMessage({ type: '', text: '' });

      const { error } = await supabase?.auth?.updateUser({
        password: security?.newPassword
      });

      if (error) throw error;

      setMessage({ type: 'success', text: 'Password updated successfully!' });
      setSecurity({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
        twoFactorEnabled: security?.twoFactorEnabled
      });
    } catch (error) {
      console.error('Error updating password:', error);
      setMessage({ type: 'error', text: error?.message || 'Failed to update password' });
    } finally {
      setSaving(false);
    }
  };

  const exportUserData = async () => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', user?.id)?.single();

      if (error) throw error;

      const dataStr = JSON.stringify(data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `growguardian-data-${new Date()?.toISOString()?.split('T')?.[0]}.json`;
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);

      setMessage({ type: 'success', text: 'Data exported successfully!' });
    } catch (error) {
      console.error('Error exporting data:', error);
      setMessage({ type: 'error', text: error?.message || 'Failed to export data' });
    } finally {
      setSaving(false);
    }
  };

  const requestDataDeletion = async () => {
    if (!window.confirm('Are you sure you want to request data deletion? This action cannot be undone.')) {
      return;
    }

    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      // In production, this would trigger a deletion request workflow
      setMessage({ 
        type: 'success', 
        text: 'Data deletion request submitted. You will receive a confirmation email within 24 hours.' 
      });
    } catch (error) {
      console.error('Error requesting deletion:', error);
      setMessage({ type: 'error', text: 'Failed to submit deletion request' });
    } finally {
      setSaving(false);
    }
  };

  const handleEnable2FA = async () => {
    try {
      // Check verification status first
      const status = await twoFactorService?.check2FAStatus(user?.id);
      
      if (security?.twoFactorMethod === 'email' && !status?.emailVerified) {
        setMessage({ 
          type: 'error', 
          text: 'Please verify your email address before enabling email-based 2FA' 
        });
        return;
      }

      if (security?.twoFactorMethod === 'sms' && !status?.phoneVerified) {
        setMessage({ 
          type: 'error', 
          text: 'Please verify your phone number before enabling SMS-based 2FA' 
        });
        return;
      }

      setTwoFactorState(prev => ({ ...prev, loading: true }));
      setMessage({ type: '', text: '' });

      let result = await twoFactorService?.enable2FA(user?.id, security?.twoFactorMethod);

      setSecurity(prev => ({ ...prev, twoFactorEnabled: true }));
      setTwoFactorState({
        setupModal: false,
        backupCodesModal: true,
        backupCodes: result?.backupCodes,
        copiedCodes: false,
        loading: false
      });

      setMessage({ 
        type: 'success', 
        text: '2FA enabled successfully! Please save your backup codes.' 
      });
    } catch (error) {
      console.error('Error enabling 2FA:', error);
      setMessage({ type: 'error', text: error?.message || 'Failed to enable 2FA' });
      setTwoFactorState(prev => ({ ...prev, loading: false }));
    }
  };

  const handleDisable2FA = async () => {
    if (!window.confirm('Are you sure you want to disable two-factor authentication? This will make your account less secure.')) {
      return;
    }

    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      await twoFactorService?.disable2FA(user?.id);

      setSecurity(prev => ({ ...prev, twoFactorEnabled: false }));
      setMessage({ type: 'success', text: '2FA disabled successfully' });
    } catch (error) {
      console.error('Error disabling 2FA:', error);
      setMessage({ type: 'error', text: error?.message || 'Failed to disable 2FA' });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate2FAMethod = async (newMethod) => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      await twoFactorService?.update2FAMethod(user?.id, newMethod);

      setSecurity(prev => ({ ...prev, twoFactorMethod: newMethod }));
      setMessage({ 
        type: 'success', 
        text: `2FA method updated to ${newMethod === 'email' ? 'Email' : 'SMS'}` 
      });
    } catch (error) {
      console.error('Error updating 2FA method:', error);
      setMessage({ type: 'error', text: error?.message || 'Failed to update 2FA method' });
    } finally {
      setSaving(false);
    }
  };

  const handleViewBackupCodes = async () => {
    try {
      setTwoFactorState(prev => ({ ...prev, loading: true }));
      const codes = await twoFactorService?.getBackupCodes(user?.id);
      setTwoFactorState({
        setupModal: false,
        backupCodesModal: true,
        backupCodes: codes,
        copiedCodes: false,
        loading: false
      });
    } catch (error) {
      console.error('Error fetching backup codes:', error);
      setMessage({ type: 'error', text: 'Failed to load backup codes' });
      setTwoFactorState(prev => ({ ...prev, loading: false }));
    }
  };

  const handleRegenerateBackupCodes = async () => {
    if (!window.confirm('This will invalidate your current backup codes. Continue?')) {
      return;
    }

    try {
      setTwoFactorState(prev => ({ ...prev, loading: true }));
      const codes = await twoFactorService?.generateBackupCodes(user?.id);
      setTwoFactorState(prev => ({
        ...prev,
        backupCodes: codes,
        copiedCodes: false,
        loading: false
      }));
      setMessage({ type: 'success', text: 'New backup codes generated' });
    } catch (error) {
      console.error('Error regenerating codes:', error);
      setMessage({ type: 'error', text: 'Failed to generate backup codes' });
      setTwoFactorState(prev => ({ ...prev, loading: false }));
    }
  };

  const handleCopyBackupCodes = () => {
    const codesText = twoFactorState?.backupCodes?.join('\n');
    navigator.clipboard?.writeText(codesText);
    setTwoFactorState(prev => ({ ...prev, copiedCodes: true }));
    setTimeout(() => {
      setTwoFactorState(prev => ({ ...prev, copiedCodes: false }));
    }, 2000);
  };

  const fetchSmsSettings = async () => {
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('sms_consent, sms_consent_updated_at, phone')
        ?.eq('id', user?.id)
        ?.single();
      if (error) throw error;
      setSmsSettings({
        smsConsent: data?.sms_consent || false,
        smsConsentUpdatedAt: data?.sms_consent_updated_at || null,
        phone: data?.phone || ''
      });
    } catch (err) {
      console.error('Error fetching SMS settings:', err);
    }
  };

  const handleSmsConsentToggle = async (newValue) => {
    if (!newValue && smsSettings?.smsConsent) {
      // Show opt-out confirmation modal
      setSmsOptOutModal(true);
      return;
    }
    await saveSmsConsent(newValue);
  };

  const saveSmsConsent = async (consentValue) => {
    try {
      setSmsLoading(true);
      setSmsMessage({ type: '', text: '' });

      if (consentValue && !profile?.phone) {
        setSmsMessage({ type: 'error', text: 'Please add a phone number before enabling SMS notifications.' });
        return;
      }

      const { error } = await supabase
        ?.from('user_profiles')
        ?.update({
          sms_consent: consentValue,
          sms_consent_updated_at: new Date()?.toISOString()
        })
        ?.eq('id', user?.id);

      if (error) throw error;

      setSmsSettings(prev => ({
        ...prev,
        smsConsent: consentValue,
        smsConsentUpdatedAt: new Date()?.toISOString()
      }));

      setSmsMessage({
        type: 'success',
        text: consentValue
          ? 'SMS consent enabled. You will receive text message alerts.' :'SMS opt-out confirmed. You will no longer receive SMS alerts.'
      });
      setSmsOptOutModal(false);
    } catch (err) {
      console.error('Error saving SMS consent:', err);
      setSmsMessage({ type: 'error', text: err?.message || 'Failed to update SMS consent.' });
    } finally {
      setSmsLoading(false);
    }
  };

  const handleStartPhoneEdit = () => {
    setSmsPhoneEdit({ isEditing: true, newPhone: profile?.phone || '', saving: false });
    setSmsMessage({ type: '', text: '' });
  };

  const handleCancelPhoneEdit = () => {
    setSmsPhoneEdit({ isEditing: false, newPhone: '', saving: false });
  };

  const handleSavePhone = async () => {
    const phone = smsPhoneEdit?.newPhone?.trim();
    if (!phone) {
      setSmsMessage({ type: 'error', text: 'Please enter a valid phone number.' });
      return;
    }
    try {
      setSmsPhoneEdit(prev => ({ ...prev, saving: true }));
      setSmsMessage({ type: '', text: '' });

      const { error } = await supabase
        ?.from('user_profiles')
        ?.update({ phone, updated_at: new Date()?.toISOString() })
        ?.eq('id', user?.id);

      if (error) throw error;

      // Update local profile state
      setProfile(prev => ({ ...prev, phone }));
      setSmsSettings(prev => ({ ...prev, phone }));
      setSmsPhoneEdit({ isEditing: false, newPhone: '', saving: false });

      // Reset phone verification since number changed
      setVerificationStatus(prev => ({ ...prev, phoneVerified: false }));

      setSmsMessage({
        type: 'success',
        text: 'Phone number updated. Please re-verify your new number to receive SMS alerts.'
      });
    } catch (err) {
      console.error('Error saving phone:', err);
      setSmsMessage({ type: 'error', text: err?.message || 'Failed to update phone number.' });
      setSmsPhoneEdit(prev => ({ ...prev, saving: false }));
    }
  };

  const handleSmsReverify = async () => {
    if (!profile?.phone) {
      setSmsMessage({ type: 'error', text: 'Please add a phone number first.' });
      return;
    }
    try {
      setSmsLoading(true);
      setSmsMessage({ type: '', text: '' });
      let result = await verificationService?.sendSmsVerification(profile?.phone);
      setVerificationModal({
        isOpen: true,
        type: 'sms',
        verificationId: result?.verificationId,
        code: ''
      });
      setSmsMessage({ type: 'success', text: 'Verification code sent via SMS.' });
    } catch (err) {
      console.error('Error sending SMS verification:', err);
      setSmsMessage({ type: 'error', text: err?.message || 'Failed to send verification code.' });
    } finally {
      setSmsLoading(false);
    }
  };

  const renderMessage = () => {
    if (!message?.text) return null;

    return (
      <div className={`mb-6 p-4 rounded-lg flex items-start gap-3 ${
        message?.type === 'success' ?'bg-green-50 text-green-800 border border-green-200' :'bg-red-50 text-red-800 border border-red-200'
      }`}>
        {message?.type === 'success' ? (
          <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
        ) : (
          <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
        )}
        <p className="text-sm font-medium">{message?.text}</p>
      </div>
    );
  };

  const renderProfileSection = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
            <Input
              type="text"
              value={profile?.full_name}
              onChange={(e) => handleProfileChange('full_name', e?.target?.value)}
              placeholder="Enter your full name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <Input
              type="email"
              value={profile?.email}
              disabled
              className="bg-gray-50"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <Input
              type="tel"
              value={profile?.phone}
              onChange={(e) => handleProfileChange('phone', e?.target?.value)}
              placeholder="Enter phone number"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <select
              value={profile?.role}
              onChange={(e) => handleProfileChange('role', e?.target?.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="user">User</option>
              <option value="manager">Manager</option>
              <option value="admin">Admin</option>
            </select>
          </div>
        </div>
      </div>

      {/* SMS Settings Section */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Smartphone className="w-5 h-5 text-green-600" />
          SMS Settings
        </h3>

        {/* SMS message feedback */}
        {smsMessage?.text && (
          <div className={`mb-4 p-3 rounded-lg flex items-start gap-2 text-sm ${
            smsMessage?.type === 'success' ?'bg-green-50 text-green-800 border border-green-200' :'bg-red-50 text-red-800 border border-red-200'
          }`}>
            {smsMessage?.type === 'success'
              ? <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              : <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />}
            <span>{smsMessage?.text}</span>
          </div>
        )}

        <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">

          {/* Phone Number Row */}
          <div className="p-4">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <Smartphone className="w-4 h-4 text-gray-500" />
                <span className="font-medium text-gray-900 text-sm">Phone Number</span>
                {verificationStatus?.phoneVerified && (
                  <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                    <CheckCircle className="w-3 h-3" /> Verified
                  </span>
                )}
                {profile?.phone && !verificationStatus?.phoneVerified && (
                  <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                    <AlertCircle className="w-3 h-3" /> Unverified
                  </span>
                )}
              </div>
              {!smsPhoneEdit?.isEditing && (
                <button
                  onClick={handleStartPhoneEdit}
                  className="flex items-center gap-1 text-xs text-green-700 hover:text-green-800 font-medium"
                >
                  <Edit2 className="w-3 h-3" /> Edit
                </button>
              )}
            </div>

            {smsPhoneEdit?.isEditing ? (
              <div className="mt-2 flex gap-2">
                <Input
                  type="tel"
                  value={smsPhoneEdit?.newPhone}
                  onChange={(e) => setSmsPhoneEdit(prev => ({ ...prev, newPhone: e?.target?.value }))}
                  placeholder="+1 (555) 000-0000"
                  className="flex-1"
                />
                <Button
                  size="sm"
                  onClick={handleSavePhone}
                  disabled={smsPhoneEdit?.saving}
                  className="flex items-center gap-1"
                >
                  <Save className="w-3 h-3" />
                  {smsPhoneEdit?.saving ? 'Saving...' : 'Save'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCancelPhoneEdit}
                  className="flex items-center gap-1"
                >
                  <X className="w-3 h-3" /> Cancel
                </Button>
              </div>
            ) : (
              <p className="text-sm text-gray-600 mt-1">
                {profile?.phone || <span className="italic text-gray-400">No phone number added</span>}
              </p>
            )}
          </div>

          {/* SMS Consent Toggle Row */}
          <div className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex-1 pr-4">
                <div className="flex items-center gap-2 mb-1">
                  <MessageSquareOff className="w-4 h-4 text-gray-500" />
                  <span className="font-medium text-gray-900 text-sm">SMS Notifications Consent</span>
                </div>
                <p className="text-xs text-gray-500">
                  By enabling, you consent to receive SMS alerts for critical pest and compliance events via Twilio.
                  Message &amp; data rates may apply. Reply STOP to opt out at any time.
                </p>
                {smsSettings?.smsConsentUpdatedAt && (
                  <p className="text-xs text-gray-400 mt-1">
                    Last updated: {new Date(smsSettings?.smsConsentUpdatedAt)?.toLocaleDateString()}
                  </p>
                )}
              </div>
              <button
                onClick={() => handleSmsConsentToggle(!smsSettings?.smsConsent)}
                disabled={smsLoading}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${
                  smsSettings?.smsConsent ? 'bg-green-600' : 'bg-gray-200'
                } ${smsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                role="switch"
                aria-checked={smsSettings?.smsConsent}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    smsSettings?.smsConsent ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Re-verification Row */}
          <div className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <RefreshCw className="w-4 h-4 text-gray-500" />
                  <span className="font-medium text-gray-900 text-sm">Re-verify Phone Number</span>
                </div>
                <p className="text-xs text-gray-500">
                  {verificationStatus?.phoneVerified
                    ? 'Your phone is verified. Re-verify if you changed your number.'
                    : 'Verify your phone to enable SMS alerts and 2FA via SMS.'}
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={handleSmsReverify}
                disabled={smsLoading || !profile?.phone}
                className="flex items-center gap-1 ml-4 flex-shrink-0"
              >
                <RefreshCw className="w-3 h-3" />
                {smsLoading ? 'Sending...' : verificationStatus?.phoneVerified ? 'Re-verify' : 'Verify'}
              </Button>
            </div>
          </div>

        </div>
      </div>

      {/* Add Verification Section */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Verification</h3>
        <div className="space-y-4">
          {/* Email Verification */}
          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-gray-600" />
              <div>
                <p className="font-medium text-gray-900">Email Address</p>
                <p className="text-sm text-gray-600">{profile?.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {verificationStatus?.emailVerified ? (
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle className="w-5 h-5" />
                  <span className="text-sm font-medium">Verified</span>
                </div>
              ) : (
                <Button
                  onClick={() => handleSendVerification('email')}
                  disabled={verificationLoading}
                  size="sm"
                  variant="outline"
                >
                  Verify Email
                </Button>
              )}
            </div>
          </div>

          {/* SMS Verification */}
          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center gap-3">
              <Bell className="w-5 h-5 text-gray-600" />
              <div>
                <p className="font-medium text-gray-900">Phone Number</p>
                <p className="text-sm text-gray-600">
                  {profile?.phone || 'Not provided'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {verificationStatus?.phoneVerified ? (
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle className="w-5 h-5" />
                  <span className="text-sm font-medium">Verified</span>
                </div>
              ) : (
                <Button
                  onClick={() => handleSendVerification('sms')}
                  disabled={verificationLoading || !profile?.phone}
                  size="sm"
                  variant="outline"
                >
                  Verify Phone
                </Button>
              )}
            </div>
          </div>

          <p className="text-sm text-gray-500">
            Verify your contact information to receive important notifications and alerts via Twilio.
          </p>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Farm/Operation Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Farm Name</label>
            <Input
              type="text"
              value={profile?.farm_name}
              onChange={(e) => handleProfileChange('farm_name', e?.target?.value)}
              placeholder="Enter farm name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Farm Size</label>
            <Input
              type="text"
              value={profile?.farm_size}
              onChange={(e) => handleProfileChange('farm_size', e?.target?.value)}
              placeholder="e.g., 50 acres"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Operation Type</label>
            <Input
              type="text"
              value={profile?.operation_type}
              onChange={(e) => handleProfileChange('operation_type', e?.target?.value)}
              placeholder="e.g., Indoor cultivation, Greenhouse"
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          onClick={saveProfileSettings}
          disabled={saving || !hasUnsavedChanges}
          className="flex items-center gap-2"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );

  const renderNotificationSection = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Communication Channels</h3>
        <div className="space-y-3">
          <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
            <input
              type="checkbox"
              checked={notifications?.email_enabled}
              onChange={(e) => handleNotificationChange('email_enabled', e?.target?.checked)}
              className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-gray-600" />
                <span className="font-medium text-gray-900">Email Notifications</span>
              </div>
              <p className="text-sm text-gray-500 mt-1">Receive updates via email</p>
            </div>
          </label>

          <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
            <input
              type="checkbox"
              checked={notifications?.sms_enabled}
              onChange={(e) => handleNotificationChange('sms_enabled', e?.target?.checked)}
              className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-gray-600" />
                <span className="font-medium text-gray-900">SMS Notifications</span>
              </div>
              <p className="text-sm text-gray-500 mt-1">Receive critical alerts via text message</p>
            </div>
          </label>

          <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
            <input
              type="checkbox"
              checked={notifications?.in_app_enabled}
              onChange={(e) => handleNotificationChange('in_app_enabled', e?.target?.checked)}
              className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-gray-600" />
                <span className="font-medium text-gray-900">In-App Notifications</span>
              </div>
              <p className="text-sm text-gray-500 mt-1">See notifications within the app</p>
            </div>
          </label>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Communication Frequency</h3>
        <select
          value={notifications?.frequency}
          onChange={(e) => handleNotificationChange('frequency', e?.target?.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
        >
          <option value="real-time">Real-time (as they happen)</option>
          <option value="daily">Daily Digest (once per day)</option>
          <option value="weekly">Weekly Summary (once per week)</option>
        </select>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Content Preferences</h3>
        <div className="space-y-2">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={notifications?.alerts_enabled}
              onChange={(e) => handleNotificationChange('alerts_enabled', e?.target?.checked)}
              className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
            />
            <span className="text-gray-700">Critical Alerts</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={notifications?.reports_enabled}
              onChange={(e) => handleNotificationChange('reports_enabled', e?.target?.checked)}
              className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
            />
            <span className="text-gray-700">Reports & Analytics</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={notifications?.updates_enabled}
              onChange={(e) => handleNotificationChange('updates_enabled', e?.target?.checked)}
              className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
            />
            <span className="text-gray-700">Product Updates</span>
          </label>
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          onClick={saveNotificationSettings}
          disabled={saving || !hasUnsavedChanges}
          className="flex items-center gap-2"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Saving...' : 'Save Preferences'}
        </Button>
      </div>
    </div>
  );

  const renderSecuritySection = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Change Password</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
            <Input
              type="password"
              value={security?.currentPassword}
              onChange={(e) => handleSecurityChange('currentPassword', e?.target?.value)}
              placeholder="Enter current password"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
            <Input
              type="password"
              value={security?.newPassword}
              onChange={(e) => handleSecurityChange('newPassword', e?.target?.value)}
              placeholder="Enter new password"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
            <Input
              type="password"
              value={security?.confirmPassword}
              onChange={(e) => handleSecurityChange('confirmPassword', e?.target?.value)}
              placeholder="Confirm new password"
            />
          </div>
          <Button
            onClick={updatePassword}
            disabled={saving || !security?.newPassword || !security?.confirmPassword}
            className="flex items-center gap-2"
          >
            <Lock className="w-4 h-4" />
            {saving ? 'Updating...' : 'Update Password'}
          </Button>
        </div>
      </div>

      {/* Enhanced 2FA Section */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Two-Factor Authentication</h3>
        
        {!security?.twoFactorEnabled ? (
          <div className="border border-gray-200 rounded-lg p-6">
            <div className="flex items-start gap-3 mb-4">
              <Shield className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h4 className="font-medium text-gray-900 mb-1">Secure Your Account</h4>
                <p className="text-sm text-gray-600 mb-4">
                  Add an extra layer of security by requiring a verification code when signing in.
                  Perfect for admin and premium accounts.
                </p>
              </div>
            </div>

            <div className="space-y-3 mb-4">
              <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="radio"
                  name="2fa-method"
                  checked={security?.twoFactorMethod === 'sms'}
                  onChange={() => handleSecurityChange('twoFactorMethod', 'sms')}
                  className="w-4 h-4 text-green-600"
                />
                <div className="flex-1">
                  <div className="font-medium text-gray-900">SMS Verification</div>
                  <p className="text-sm text-gray-600">Receive codes via text message</p>
                  {!verificationStatus?.phoneVerified && (
                    <p className="text-xs text-amber-600 mt-1">⚠️ Phone number must be verified first</p>
                  )}
                </div>
              </label>

              <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="radio"
                  name="2fa-method"
                  checked={security?.twoFactorMethod === 'email'}
                  onChange={() => handleSecurityChange('twoFactorMethod', 'email')}
                  className="w-4 h-4 text-green-600"
                />
                <div className="flex-1">
                  <div className="font-medium text-gray-900">Email Verification</div>
                  <p className="text-sm text-gray-600">Receive codes via email</p>
                  {!verificationStatus?.emailVerified && (
                    <p className="text-xs text-amber-600 mt-1">⚠️ Email must be verified first</p>
                  )}
                </div>
              </label>
            </div>

            <Button
              onClick={handleEnable2FA}
              disabled={twoFactorState?.loading}
              className="w-full flex items-center justify-center gap-2"
            >
              <Shield className="w-4 h-4" />
              {twoFactorState?.loading ? 'Setting up...' : 'Enable 2FA'}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="border border-green-200 bg-green-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="font-medium text-green-900">2FA is Active</span>
              </div>
              <p className="text-sm text-green-700">
                Your account is protected with {security?.twoFactorMethod === 'email' ? 'email' : 'SMS'} verification
              </p>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Verification Method
                </label>
                <div className="space-y-2">
                  <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="radio"
                      name="2fa-method-change"
                      checked={security?.twoFactorMethod === 'sms'}
                      onChange={() => handleUpdate2FAMethod('sms')}
                      disabled={!verificationStatus?.phoneVerified || saving}
                      className="w-4 h-4 text-green-600"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">SMS</div>
                      {!verificationStatus?.phoneVerified && (
                        <p className="text-xs text-amber-600">Phone must be verified</p>
                      )}
                    </div>
                  </label>

                  <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="radio"
                      name="2fa-method-change"
                      checked={security?.twoFactorMethod === 'email'}
                      onChange={() => handleUpdate2FAMethod('email')}
                      disabled={!verificationStatus?.emailVerified || saving}
                      className="w-4 h-4 text-green-600"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">Email</div>
                      {!verificationStatus?.emailVerified && (
                        <p className="text-xs text-amber-600">Email must be verified</p>
                      )}
                    </div>
                  </label>
                </div>
              </div>

              <Button
                onClick={handleViewBackupCodes}
                disabled={twoFactorState?.loading}
                variant="outline"
                className="w-full flex items-center justify-center gap-2"
              >
                <Key className="w-4 h-4" />
                {twoFactorState?.loading ? 'Loading...' : 'View Backup Codes'}
              </Button>

              <Button
                onClick={handleDisable2FA}
                disabled={saving}
                variant="outline"
                className="w-full text-red-700 border-red-300 hover:bg-red-50"
              >
                {saving ? 'Disabling...' : 'Disable 2FA'}
              </Button>
            </div>
          </div>
        )}
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Login History</h3>
        <div className="border border-gray-200 rounded-lg p-4">
          <p className="text-sm text-gray-600">
            Last login: {new Date()?.toLocaleDateString()} at {new Date()?.toLocaleTimeString()}
          </p>
          <p className="text-sm text-gray-600 mt-1">
            Device: {navigator.userAgent?.includes('Mobile') ? 'Mobile' : 'Desktop'}
          </p>
        </div>
      </div>
    </div>
  );

  const renderDataManagementSection = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Data Export</h3>
        <p className="text-sm text-gray-600 mb-4">
          Download a copy of your GrowGuardian data in JSON format
        </p>
        <Button
          onClick={exportUserData}
          disabled={saving}
          variant="outline"
          className="flex items-center gap-2"
        >
          <Database className="w-4 h-4" />
          {saving ? 'Exporting...' : 'Export My Data'}
        </Button>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Data Retention</h3>
        <select
          value={dataManagement?.retentionPeriod}
          onChange={(e) => handleDataManagementChange('retentionPeriod', e?.target?.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
        >
          <option value="6-months">6 Months</option>
          <option value="1-year">1 Year</option>
          <option value="2-years">2 Years</option>
          <option value="indefinite">Indefinite</option>
        </select>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Privacy Settings</h3>
        <div className="space-y-3">
          <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
            <input
              type="checkbox"
              checked={dataManagement?.dataSharing}
              onChange={(e) => handleDataManagementChange('dataSharing', e?.target?.checked)}
              className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
            />
            <div className="flex-1">
              <span className="font-medium text-gray-900">Allow Data Sharing</span>
              <p className="text-sm text-gray-500 mt-1">Share anonymized data to improve our services</p>
            </div>
          </label>

          <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
            <input
              type="checkbox"
              checked={dataManagement?.analyticsEnabled}
              onChange={(e) => handleDataManagementChange('analyticsEnabled', e?.target?.checked)}
              className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
            />
            <div className="flex-1">
              <span className="font-medium text-gray-900">Analytics & Performance</span>
              <p className="text-sm text-gray-500 mt-1">Help us understand how you use GrowGuardian</p>
            </div>
          </label>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">GDPR Compliance</h3>
        <div className="border border-red-200 bg-red-50 rounded-lg p-4">
          <p className="text-sm text-red-800 mb-3">
            Request permanent deletion of all your data. This action cannot be undone.
          </p>
          <Button
            onClick={requestDataDeletion}
            disabled={saving}
            variant="outline"
            className="text-red-700 border-red-300 hover:bg-red-100"
          >
            {saving ? 'Processing...' : 'Request Data Deletion'}
          </Button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading settings...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">Settings & Preferences</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-2">Manage your account settings and preferences</p>
        </div>

        {/* Message Display */}
        {renderMessage()}

        {/* SMS Opt-Out Confirmation Modal */}
        {smsOptOutModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <MessageSquareOff className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Opt Out of SMS Alerts?</h3>
                  <p className="text-sm text-gray-500">This will disable all SMS notifications</p>
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-5">
                <p className="text-sm text-amber-800">
                  <strong>Warning:</strong> Opting out will stop all SMS alerts including critical pest warnings,
                  compliance reminders, and security codes. You can re-enable at any time.
                </p>
              </div>
              <p className="text-sm text-gray-600 mb-5">
                By confirming, you consent to opt out of SMS communications from GrowGuardian.
                You may re-enable SMS notifications at any time from this settings page.
              </p>
              <div className="flex gap-3">
                <Button
                  onClick={() => saveSmsConsent(false)}
                  disabled={smsLoading}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  {smsLoading ? 'Processing...' : 'Confirm Opt-Out'}
                </Button>
                <Button
                  onClick={() => setSmsOptOutModal(false)}
                  variant="outline"
                  className="flex-1"
                  disabled={smsLoading}
                >
                  Keep SMS Enabled
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Verification Modal */}
        {verificationModal?.isOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Enter Verification Code
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                We sent a code to your {verificationModal?.type === 'email' ? 'email' : 'phone'}.
              </p>
              <Input
                type="text"
                value={verificationModal?.code}
                onChange={(e) => setVerificationModal(prev => ({ ...prev, code: e?.target?.value }))}
                placeholder="Enter 6-digit code"
                className="mb-4"
              />
              <div className="flex gap-3">
                <Button
                  onClick={handleVerifyCode}
                  disabled={verificationLoading || !verificationModal?.code}
                  className="flex-1"
                >
                  {verificationLoading ? 'Verifying...' : 'Verify'}
                </Button>
                <Button
                  onClick={() => setVerificationModal({ isOpen: false, type: null, verificationId: null, code: '' })}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* 2FA Backup Codes Modal */}
        {twoFactorState?.backupCodesModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <div className="flex items-center gap-2 mb-4">
                <Key className="w-5 h-5 text-green-600" />
                <h3 className="text-lg font-semibold text-gray-900">Backup Codes</h3>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Save these codes in a secure location. Each code can only be used once.
              </p>
              <div className="bg-gray-50 rounded-lg p-4 mb-4 font-mono text-sm">
                {twoFactorState?.backupCodes?.map((code, idx) => (
                  <div key={idx} className="py-1">{code}</div>
                ))}
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={handleCopyBackupCodes}
                  variant="outline"
                  className="flex-1 flex items-center justify-center gap-2"
                >
                  {twoFactorState?.copiedCodes ? (
                    <><Check className="w-4 h-4" /> Copied!</>
                  ) : (
                    'Copy Codes'
                  )}
                </Button>
                <Button
                  onClick={handleRegenerateBackupCodes}
                  variant="outline"
                  className="flex-1"
                  disabled={twoFactorState?.loading}
                >
                  Regenerate
                </Button>
              </div>
              <Button
                onClick={() => setTwoFactorState(prev => ({ ...prev, backupCodesModal: false }))}
                className="w-full mt-3"
              >
                Done
              </Button>
            </div>
          </div>
        )}

        {/* Tabs Navigation */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex overflow-x-auto">
              <button
                onClick={() => setActiveSection('profile')}
                className={`px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeSection === 'profile' ?'border-green-600 text-green-600' :'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  <span>Profile</span>
                </div>
              </button>
              <button
                onClick={() => setActiveSection('notifications')}
                className={`px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeSection === 'notifications'
                    ? 'border-green-600 text-green-600' :'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Bell className="w-4 h-4" />
                  <span>Notifications</span>
                </div>
              </button>
              <button
                onClick={() => setActiveSection('security')}
                className={`px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeSection === 'security' ?'border-green-600 text-green-600' :'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  <span>Security</span>
                </div>
              </button>
              <button
                onClick={() => setActiveSection('data')}
                className={`px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeSection === 'data' ?'border-green-600 text-green-600' :'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Database className="w-4 h-4" />
                  <span>Data Management</span>
                </div>
              </button>
            </nav>
          </div>
        </div>

        {/* Content Sections */}
        <div className="bg-white rounded-lg shadow p-6">
          {activeSection === 'profile' && renderProfileSection()}
          {activeSection === 'notifications' && renderNotificationSection()}
          {activeSection === 'security' && renderSecuritySection()}
          {activeSection === 'data' && renderDataManagementSection()}
        </div>
      </div>
    </div>
  );
};

export default UserSettingsPreferences;